import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import ButtonBase from '@material-ui/core/ButtonBase';
import * as colors from '@constants/colors';
import BadgeIcon from '@shared/badge-icon';

// Replaces material-ui/DatePicker/dateUtils isEqualDate
const isEqualDate = (d1, d2) => (
  d1 && d2 &&
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate()
);

// Replaces material-ui/styles/transitions.easeOut()
const easeOutTransition = 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms';

const getStyles = (date, disabled, excluded, selected, hover) => {
  // Theme color mappings:
  // baseTheme.palette.textColor -> colors.dotmapsGray
  // datePicker.selectTextColor -> white
  // datePicker.selectColor -> colors.dotmapsLightBlue
  // datePicker.color -> colors.dotmapsBlue
  let labelColor = colors.dotmapsGray;
  let buttonStateOpacity = 0;
  let buttonStateTransform = 'scale(0)';

  if (hover || selected) {
    labelColor = '#FFFFFF';
    buttonStateOpacity = selected ? 1 : 0.6;
    buttonStateTransform = 'scale(1)';
  } else if (isEqualDate(date, new Date())) {
    labelColor = colors.dotmapsBlue;
  }

  if (excluded) {
    buttonStateOpacity = 0.6;
  }

  return {
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      fontWeight: '400',
      opacity: disabled ? '0.4' : null,
      padding: '4px 0px',
      position: 'relative',
      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
      width: 42
    },
    label: {
      color: labelColor,
      fontWeight: '400',
      position: 'relative',
      fontSize: '0.75rem',
      width: '1.6rem',
      height: '1.6rem'
    },
    buttonState: {
      backgroundColor: colors.dotmapsLightBlue,
      borderRadius: '50%',
      height: 34,
      left: 4,
      opacity: buttonStateOpacity,
      position: 'absolute',
      top: -4,
      transform: buttonStateTransform,
      transition: easeOutTransition,
      width: 34
    }
  };
};

const DayButton = ({
  DateTimeFormat,
  date,
  disabled = false,
  excluded = false,
  locale,
  onClick,
  selected = false
}) => {
  const [hover, setHover] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (!disabled) {
      setHover(true);
    }
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    if (!disabled) {
      setHover(false);
    }
  }, [disabled]);

  const handleClick = useCallback((event) => {
    if (!disabled && onClick) {
      onClick(event, date);
    }
  }, [disabled, onClick, date]);

  const styles = getStyles(date, disabled, excluded, selected, hover);

  const renderLabel = () => {
    const label = (
      <span style={styles.label}>
        {new DateTimeFormat(locale, { day: 'numeric' }).format(date)}
      </span>
    );
    if (excluded) {
      return <BadgeIcon size={16}>{label}</BadgeIcon>;
    }
    return label;
  };

  if (!date) {
    return <span style={styles.root} />;
  }

  return (
    <ButtonBase
      disabled={disabled}
      disableRipple
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      style={styles.root}
    >
      <div style={styles.buttonState} />
      {renderLabel()}
    </ButtonBase>
  );
};

DayButton.propTypes = {
  DateTimeFormat: PropTypes.func.isRequired,
  date: PropTypes.object,
  disabled: PropTypes.bool,
  excluded: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  selected: PropTypes.bool
};

export default DayButton;
